import React, { useState } from "react";

import { Modal } from "reactstrap";
import { toast } from "react-toastify";

import "assets/css/home-modern.css";
import "assets/css/flow.css";

// core components
import DefaultFooter from "components/Footers/DefaultFooter.js";
import IndexNavbar from "components/Navbars/IndexNavbar";
import FlowHero from "components/Flow/FlowHero";
import FlowEmpty from "components/Flow/FlowEmpty";
import SummaryBar from "components/Flow/SummaryBar";
import ProductSearch from "components/Flow/ProductSearch";
import { useBusySpan } from "hooks/useBusy";
import { APIRequest, contendedProducts, takeContendedHandover } from "utils/ApiRequest";
import SponsorsModal from "components/Sponsors/SponsorsModal";
import { filterProducts, hasQuery } from "utils/productSearch";
import { currentPujoYear, sponsorsFromProducts } from "utils/sponsors";
import { formatINR } from "utils/format";
import { useNavigate } from "react-router";
import { Link } from "react-router-dom";

import heroImage from "assets/img/banner2.jpg";

const FLOW_STEPS = ["Membership", "Sponsorship", "Review & pay"];

function SponsorshipPage() {
  const [sponsorshipProducts, setSponsorshipProducts] = React.useState(null);
  const [selectedPrds, setSelectedPrds] = useState({});
  const [totalCartPrice, setTotalCartPrice] = useState(0);
  const [token, setToken] = useState(null);
  const [hideAlreadySponsored, setHideAlreadySponsored] = useState(true);
  const [query, setQuery] = useState("");
  const [modalLive, setModalLive] = React.useState(false);
  const [contended, setContended] = useState({});
  const [sponsorsOpen, setSponsorsOpen] = useState(false);
  // Which tile is showing its description. Only ever one — the panel covers the card face, and two
  // open at once would just be two cards a contributor has to dismiss.
  const [infoFor, setInfoFor] = useState(null);
  const setBusy = useBusySpan();
  const history = useNavigate();

  React.useEffect(() => {
    document.body.classList.add("sidebar-collapse");
    document.documentElement.classList.remove("nav-open");
    window.scrollTo(0, 0);
    document.body.scrollTop = 0;

    let userInfo = localStorage.getItem('user-info');
    if (userInfo !== null) {
      userInfo = JSON.parse(userInfo);
      if (userInfo.session !== null) {
        setToken(userInfo.session);
        loadProducts(userInfo.session);
        let sPrd = localStorage.getItem('selectedPrds');
        if (sPrd !== null) {
          setSelectedPrds(JSON.parse(sPrd));
        }
        let tcp = localStorage.getItem('totalCartPrice');
        setTotalCartPrice(tcp !== null ? parseInt(tcp) : 0);
        // Sent back here by a rejected /startOrder — mark those tiles straight away.
        markContended(takeContendedHandover());
      } else {
        history("/login")
      }
    } else {
      history("/login")
    }

    return function cleanup() {
      document.body.classList.remove("sidebar-collapse");
    };
  }, []);

  function loadProducts(token) {
    APIRequest(
      token,
      "/getProducts",
      {},
      (res) => {
        const products = res.data.productsResponses != null
          ? res.data.productsResponses["SPONSORSHIP"]
          : null;
        setSponsorshipProducts(products != null ? products : []);
      },
      () => {
        setSponsorshipProducts([]);
      }
    );
  }

  // The total is summed from the whole selection, the way MembershipPage does it, rather than
  // nudged by a delta off a `let tcp = 0` baseline. The delta version dropped to zero whenever a
  // branch did not fire — re-ticking an item already restored from localStorage wiped the cart —
  // and it drifted apart from the map it was meant to describe once more than one item was picked.
  // The map is replaced rather than mutated so React actually sees the change; the old code passed
  // the same reference back to setSelectedPrds and only re-rendered because the price setter did.
  React.useEffect(() => {
    if (infoFor == null) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setInfoFor(null);
    };
    document.addEventListener("keydown", onKey);
    return function cleanup() {
      document.removeEventListener("keydown", onKey);
    };
  }, [infoFor]);

  const handleSponsorClick = (event, fullSponsor, productId, code, price) => {
    const checked = event.target.checked === true;
    const next = { ...selectedPrds };
    if (checked) {
      next[productId] = { qty: 1, code, price, fullSponsor };
    } else {
      delete next[productId];
    }
    const tcp = Object.keys(next).reduce((sum, key) => sum + (next[key].price || 0), 0);

    setSelectedPrds(next);
    setTotalCartPrice(tcp);
    setContended({});
    localStorage.setItem('selectedPrds', JSON.stringify(next));
    localStorage.setItem('totalCartPrice', tcp);
  }

  // Everything the server said was wrong with the cart, keyed by product id, so each offending tile
  // can say so itself. The page used to show only err.data.desc — one sentence naming nothing — and
  // the server only ever reported the first offender anyway, so a contributor holding three blocked
  // items cleared them one failed payment at a time.
  const markContended = (items) => {
    const flagged = {};
    items.forEach((p) => {
      flagged[p.id] = { name: p.name, retriable: p.retriable };
    });
    setContended(flagged);
    return items.length;
  };

  const handleReviewSelection = (e, isForced) => {
    e.preventDefault();
    setBusy("Preparing your summary…");
    APIRequest(
      token,
      "/addToCart",
      { productQtys: selectedPrds },
      (res) => {
        setBusy(null);
        setContended({});
        history("/summary");
      },
      (err) => {
        setBusy(null);
        try {
          const flaggedCount = markContended(contendedProducts(err));
          if (flaggedCount > 0) {
            // The tiles carry the detail; the toast only has to send them back to the list.
            return toast.error(
              flaggedCount === 1
                ? "One item in your selection is no longer yours to take — it is marked below."
                : flaggedCount + " items in your selection are no longer yours to take — they are marked below."
            );
          }
          return toast.error(err.data.desc);
        } catch (error) {
          console.log(error);
          toast.error('Seems some internal issue with your network, please check your network!');
        }
      }
    );

  };

  /**
   * Some items are priced the same either way — the Bhog Thali is 5,001 whether you take it whole
   * or share it. Offering "Full sponsor 5,001" and "Co-sponsor 5,001" is a choice with no
   * consequence, and showing only "Co-sponsor" implies a discount that is not there. Those get one
   * plain line instead.
   *
   * The request still goes up as a co-sponsorship, which is not cosmetic: CartHandler sums the
   * full-sponsor lines to decide who has crossed the 15,000 / 35,000 thresholds that waive UTSAV
   * membership fees, so counting a shared item there would hand out free memberships it was never
   * meant to pay for.
   *
   * Items flagged fullSponsorOnly are left alone — they are deliberately not shareable, and their
   * value should keep counting toward those thresholds.
   */
  const isSingleRate = (mprd) =>
    mprd.fullSponsorOnly !== true &&
    mprd.price != null &&
    mprd.coPrice != null &&
    Number(mprd.price) > 0 &&
    Number(mprd.price) === Number(mprd.coPrice);

  const visibleProducts = sponsorshipProducts != null
    ? sponsorshipProducts.filter((mprd) =>
      (mprd.available || !hideAlreadySponsored) &&
      (mprd.available || !(mprd.sponsoredBy === undefined || mprd.sponsoredBy === null || mprd.sponsoredBy === "")))
    : null;

  const isSearching = hasQuery(query);

  // Search deliberately ignores the Available/All toggle and runs over the whole
  // catalogue — otherwise items that are neither open nor sponsored yet are
  // unreachable in both modes. Sponsor names are searchable too, so a search can
  // answer "who sponsored X". Clearing the search hands control back to the toggle.
  const matchedProducts = isSearching
    ? filterProducts(sponsorshipProducts, query, ["name", "desc", "code", "sponsoredBy"])
    : visibleProducts;

  // The single-rate items are the ones bought in numbers — a thali or a chandipath is taken by many
  // people, where the rest of the catalogue is one offering to one patron. Left in id order they sat
  // wherever the product table happened to put them, several rows down. Sort is stable, so
  // everything else keeps the order it already had, and this applies to search results too.
  const searchedProducts = matchedProducts == null
    ? null
    : [...matchedProducts].sort(
      (a, b) => (isSingleRate(b) ? 1 : 0) - (isSingleRate(a) ? 1 : 0)
    );

  const openCount = sponsorshipProducts != null
    ? sponsorshipProducts.filter((mprd) => mprd.available).length
    : 0;

  const pickedCount = Object.keys(selectedPrds)
    .filter((key) => selectedPrds[key].fullSponsor !== undefined).length;

  const renderOption = (mprd, isFull, single) => {
    const picked = selectedPrds[mprd.id];
    const decided = picked != null && picked.fullSponsor !== undefined && picked.fullSponsor !== null;
    const isThisOne = decided && Boolean(picked.fullSponsor) === isFull;
    const amount = isFull ? mprd.price : mprd.coPrice;
    return (
      <label className="hm-opt">
        {/* Controlled, so the box always shows what is actually in the cart. As an uncontrolled
            defaultChecked it only reflected the DOM: React applies that on mount alone, so a
            selection restored from localStorage or cleared after a failed order left the tick
            behind. That is only safe now the selection map is replaced rather than mutated. */}
        <input
          type="checkbox"
          checked={isThisOne}
          disabled={decided && !isThisOne}
          onChange={(e) => {
            handleSponsorClick(e, isFull, mprd.id, mprd.code, amount)
          }}
        />
        <span className="hm-opt-text">
          <span className="hm-opt-name">
            {single ? "Sponsor this" : isFull ? "Full sponsor" : "Co-sponsor"}
          </span>
          <span className="hm-opt-amt">&#x20B9;{formatINR(amount)}</span>
        </span>
      </label>
    );
  };

  // Built from the products this page already loaded, so seeing who has sponsored costs no extra
  // request and cannot disagree with the grid the visitor is looking at.
  const sponsorsYear = currentPujoYear();
  const sponsorsData = sponsorsFromProducts(sponsorshipProducts, sponsorsYear);
  const sponsoredCount =
    sponsorsData.fullSponsors.length + sponsorsData.coSponsors.length;

  return (
    <>
      <IndexNavbar />
      <div className="wrapper">
        <FlowHero
          image={heroImage}
          eyebrow={"Step 2 of " + FLOW_STEPS.length}
          title="Choose your sponsorship"
          lead="Sponsorship is entirely optional — but every contribution goes straight into making this Durgotsav grander for the whole community."
          steps={FLOW_STEPS}
          activeStep={1}
        />
        <div className="main hm hm-flow">
          <section className="hm-fbody">
            <div className="hm-wrap">
              {visibleProducts == null && (
                <div className="hm-fstate">
                  <span className="hm-spinner" aria-hidden="true"></span>
                  <p style={{ margin: 0 }}>Loading sponsorship options…</p>
                </div>
              )}

              {sponsorshipProducts != null && sponsorshipProducts.length === 0 && (
                <FlowEmpty
                  icon="now-ui-icons ui-1_calendar-60"
                  title="Sponsorship has not opened yet"
                  message="Sponsorship items for this year are not live yet. You can continue with your membership now and come back to sponsor once they are announced."
                >
                  <Link className="hm-btn hm-btn-primary" to="/membership">
                    Back to membership
                  </Link>
                  <Link className="hm-btn hm-btn-outline" to="/index">
                    Go to home
                  </Link>
                </FlowEmpty>
              )}

              {sponsorshipProducts != null && sponsorshipProducts.length > 0 && (
                <>
                  <div className="hm-notice hm-notice-slim">
                    <span className="hm-notice-ico" aria-hidden="true">
                      <i className="now-ui-icons objects_diamond"></i>
                    </span>
                    <div className="hm-notice-body">
                      <h3>How sponsorship works</h3>
                      <ul>
                        <li>
                          <b>Full sponsor</b> — the item carries your name alone.
                        </li>
                        <li>
                          <b>Co-sponsor</b> — share an item with other members at a lower contribution.
                        </li>
                        <li>
                          Full sponsorships of <b>&#x20B9;15,000+</b> waive UTSAV membership fees for up to
                          4 family members, and <b>&#x20B9;35,000+</b> for up to 6.
                        </li>
                      </ul>
                    </div>
                  </div>

                  <div className="hm-fsec-bar">
                    <div>
                      <h2 className="hm-fsec-title">Sponsorship items</h2>
                      <p className="hm-fsec-sub">
                        {openCount} of {sponsorshipProducts.length} items still open for this year
                        {sponsoredCount > 0 && (
                          <>
                            {" \u00b7 "}
                            <button
                              className="hm-linkbtn"
                              type="button"
                              onClick={() => setSponsorsOpen(true)}
                            >
                              See our {sponsorsYear} sponsors
                            </button>
                          </>
                        )}
                      </p>
                    </div>
                    <div
                      className="hm-seg"
                      role="group"
                      aria-label="Filter sponsorship items"
                      title={isSearching ? "Search covers every item — clear it to filter again" : undefined}
                    >
                      <button
                        type="button"
                        className={hideAlreadySponsored ? "is-on" : ""}
                        disabled={isSearching}
                        onClick={() => setHideAlreadySponsored(true)}
                      >
                        Available
                      </button>
                      <button
                        type="button"
                        className={!hideAlreadySponsored ? "is-on" : ""}
                        disabled={isSearching}
                        onClick={() => setHideAlreadySponsored(false)}
                      >
                        All items
                      </button>
                    </div>
                  </div>

                  <ProductSearch
                    id="sponsorship-search"
                    value={query}
                    onChange={setQuery}
                    placeholder="Search every item by name, description or sponsor…"
                    label="Search sponsorship items"
                    noun={sponsorshipProducts.length === 1 ? "item" : "items"}
                    resultCount={searchedProducts.length}
                    totalCount={isSearching ? sponsorshipProducts.length : visibleProducts.length}
                    hint={isSearching ? "Searching the full catalogue, including items not open yet" : null}
                  />

                  {!isSearching && visibleProducts.length === 0 && (
                    <div className="hm-fstate">
                      <p style={{ margin: 0 }}>
                        {openCount === 0
                          ? "No sponsorship item is open at the moment. Search above to look one up anyway."
                          : "Every item has been sponsored already. Switch to All items to see who sponsored what."}
                      </p>
                    </div>
                  )}

                  {isSearching && searchedProducts.length === 0 && (
                    <div className="hm-fstate">
                      <p style={{ margin: 0 }}>
                        No sponsorship item matches <b>“{query.trim()}”</b> — searched all{" "}
                        {sponsorshipProducts.length} items.
                      </p>
                      <button className="hm-btn hm-btn-outline" type="button" onClick={() => setQuery("")}>
                        Clear search
                      </button>
                    </div>
                  )}

                  {searchedProducts.length > 0 && (
                    <div className="hm-igrid">
                      {searchedProducts.map((mprd) => {
                        const picked = selectedPrds[mprd.id];
                        const isPicked = picked != null && picked.fullSponsor !== undefined;
                        const clash = contended[mprd.id];
                        // An item held for this member reads as available and carries their own
                        // name, so without the badge the tile is indistinguishable from an open one
                        // — and "Sponsored by <your name>" on its own just looks like it is gone.
                        const heldForMe = mprd.reservedForYou === true;
                        const takenBy = !heldForMe
                          && mprd.sponsoredBy !== undefined
                          && mprd.sponsoredBy !== null
                          && mprd.sponsoredBy !== ""
                          ? mprd.sponsoredBy
                          : null;
                        return (
                          <article
                            className={
                              "hm-tile" +
                              (isPicked ? " is-picked" : "") +
                              (clash != null ? " is-clashed" : "") +
                              (!mprd.available ? " is-taken" : "")
                            }
                            key={mprd.id}
                          >
                            <div className="hm-tile-head">
                              {heldForMe && (
                                <span className="hm-held">Reserved for you</span>
                              )}
                              <div className="hm-tile-title">
                                <h3 className="hm-tile-name">{mprd.name}</h3>
                                {mprd.desc && (
                                  <button
                                    className="hm-tile-i"
                                    type="button"
                                    aria-expanded={infoFor === mprd.id}
                                    aria-controls={"tileInfo" + mprd.id}
                                    aria-label={"About " + mprd.name}
                                    onClick={() => setInfoFor(infoFor === mprd.id ? null : mprd.id)}
                                  >
                                    <i className="now-ui-icons travel_info" aria-hidden="true"></i>
                                  </button>
                                )}
                              </div>
                            </div>
                            {mprd.desc && infoFor === mprd.id && (
                              <div className="hm-tile-info" id={"tileInfo" + mprd.id}>
                                <div className="hm-tile-info-head">
                                  <span>About this offering</span>
                                  <button
                                    className="hm-tile-info-x"
                                    type="button"
                                    aria-label="Close"
                                    onClick={() => setInfoFor(null)}
                                  >
                                    <span aria-hidden={true}>&times;</span>
                                  </button>
                                </div>
                                <p>{mprd.desc}</p>
                              </div>
                            )}
                            {mprd.available && (
                              <div className="hm-tile-body">
                                <div className="hm-opts">
                                  {isSingleRate(mprd) ? (
                                    renderOption(mprd, false, true)
                                  ) : (
                                    <>
                                      {!mprd.coSponsored && renderOption(mprd, true)}
                                      {!mprd.fullSponsorOnly && renderOption(mprd, false)}
                                    </>
                                  )}
                                </div>
                              </div>
                            )}
                            {clash != null && (
                              <div className="hm-taken is-clash">
                                {clash.retriable
                                  ? "Someone is paying for this right now — try again in a few minutes."
                                  : "This one has just been sponsored. Untick it to carry on."}
                              </div>
                            )}
                            {clash == null && !mprd.available && takenBy !== null && (
                              <div className="hm-taken">
                                Sponsored by <b>{takenBy}</b> · &#x20B9;{formatINR(mprd.price)}
                              </div>
                            )}
                            {clash == null && !mprd.available && takenBy === null && (
                              <div className="hm-taken is-soon">
                                Not open for sponsorship yet · &#x20B9;{formatINR(mprd.price)}
                              </div>
                            )}
                          </article>
                        );
                      })}
                    </div>
                  )}

                  <SummaryBar
                    label="Cart total"
                    total={totalCartPrice}
                    note={
                      pickedCount > 0
                        ? pickedCount + (pickedCount === 1 ? " sponsorship added" : " sponsorships added")
                        : "Sponsorship is optional — you can review and pay without one"
                    }
                  >
                    <Link className="hm-btn hm-btn-outline hm-btn-quiet" to="/membership">
                      Replan membership
                    </Link>
                    <button
                      className="hm-btn hm-btn-primary"
                      type="button"
                      onClick={(e) => {
                        handleReviewSelection(e, false)
                      }}
                    >
                      Review your selection <i className="now-ui-icons arrows-1_minimal-right"></i>
                    </button>
                  </SummaryBar>
                </>
              )}
            </div>
          </section>
        </div>
        <DefaultFooter />
      </div>

      {sponsorsOpen && (
        <SponsorsModal
          data={sponsorsData}
          year={sponsorsYear}
          onClose={() => setSponsorsOpen(false)}
        />
      )}

      <Modal
        isOpen={modalLive}
        centered
        contentClassName="hm-modal"
        toggle={() => setModalLive(false)}
      >
        <div className="hm-modal-head">
          <h5 className="hm-modal-title">Proceed without a membership?</h5>
          <button
            aria-label="Close"
            className="hm-modal-x"
            type="button"
            onClick={() => setModalLive(false)}
          >
            <span aria-hidden={true}>×</span>
          </button>
        </div>
        <div className="hm-modal-body">
          <p>
            You have not selected <b>any membership</b> for you and your family — without it you will
            not be able to avail the members' exclusive benefits.
          </p>
        </div>
        <div className="hm-modal-foot">
          <button className="hm-btn hm-btn-outline" type="button" onClick={() => setModalLive(false)}>
            No, select membership
          </button>
          <button
            className="hm-btn hm-btn-primary"
            type="button"
            onClick={(e) => handleReviewSelection(e, true)}
          >
            Yes, proceed
          </button>
        </div>
      </Modal>

    </>
  );
}

export default SponsorshipPage;
